import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useSelection } from 'src/context';
import { Loader } from '~/components';
import { CreatePDF } from '~/graphql';
import { ReportFormData } from '.';

export const CreateReportForm = () => {
    const methods = useForm<ReportFormData>();
    const { handleSubmit, register } = methods;
    const [createPDF, { loading }] = useMutation(CreatePDF);
    const { selectedContent, getSelectedIndexesFromContent } = useSelection();
    const [error, setError] = useState('');
    const [downloaded, setDownloaded] = useState(false);

    const onSubmit: SubmitHandler<ReportFormData> = async formData => {
        setError('');
        // console.log('selectedContent', selectedContent);
        // just pass selected question indexes, not answers to avoid big payload
        const selectedIndexes = getSelectedIndexesFromContent(selectedContent);
        const input = { reportFormData: formData, selectedQuestions: JSON.stringify(selectedIndexes) };
        try {
            const { data: pdfData } = await createPDF({ variables: { input } });
            const response = pdfData?.createPDF;
            if (!response) {
                setError('Unexpected error. Please try again.');
                return;
            }
            if (response.warning) {
                setError(response.warning);
            }
            if (response.url) {
                downloadFile(response.url, 'Due Diligence Report.pdf');
            }
            setDownloaded(true);
        } catch (e) {
            setError('Something went wrong. Please try again.');
        }
    };

    return (
        <div>
            {downloaded ? (
                <div className="create-report-form">
                    <h3 className="text-center text-white xl:text-2xl">Thank you</h3>
                    <article className="text-center">
                        <p>Please look in your downloads folder.</p>
                    </article>
                </div>
            ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="create-report-form">
                    <h3 className="text-center text-white xl:text-2xl">Create your tailored report</h3>
                    <article>
                        <p>To create your report, fill in your details below and click “Download report”.</p>
                    </article>

                    <input className="form-input" required placeholder="Name *" {...register('name')} />

                    <input className="form-input" required placeholder="Company *" {...register('company')} />

                    <input className="form-input" required placeholder="FCA number *" {...register('fcaNumber')} />

                    <input className="form-input" required placeholder="Phone *" {...register('phone')} />

                    <input className="form-input" required placeholder="Postcode *" {...register('postcode')} />

                    <input className="form-input" placeholder="BDM Name (if you have one)" {...register('bdmName')} />

                    <input className="form-input" required placeholder="Email *" {...register('email')} />

                    <p className="text-sm">
                        By downloading the report, you agree to Scottish Widows processing your data in accordance with
                        our{' '}
                        <a
                            href="https://platform.scottishwidows.co.uk/short-adviser-dpn/"
                            target="_blank"
                            className="link"
                        >
                            data privacy notice
                        </a>
                        . We may contact you to discuss how we can help with your due diligence needs.
                    </p>

                    <button
                        className="button btn-primary m-auto mb-4 flex w-40 items-center justify-center"
                        type="submit"
                        disabled={loading}
                    >
                        {loading ? <Loader /> : 'Download report'}
                    </button>

                    {error && (
                        <div className="rounded-md bg-amber-500 p-3">
                            <p>{error}</p>
                        </div>
                    )}
                </form>
            )}
        </div>
    );
};

const downloadFile = (url: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
